type FieldType =
  | "email"
  | "password"
  | "text"
  | "confirmPassword"
  | "numberOnly"
  | "file"
  | "date"
  | "array"
  | "editor";

export interface ValidationField {
  key: string;
  value: string | File | null | any[] | number;
  type: FieldType;
  minLength?: number;
  maxLength?: number;
  customPattern?: string;
  customError?: string;
  fileType?: "image" | "pdf" | "doc" | "video" | string;
  range?: string;
}

export type ValidationResult = {
  errors: Record<string, string>;
  isValid: boolean;
};

const patterns: Record<
  Exclude<FieldType, "confirmPassword" | "file" | "date" | "array" | "editor">, // <-- exclude editor from regex patterns
  { regex: RegExp; error: string }
> = {
  email: {
    regex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    error: "Invalid email address.",
  },
  password: {
    regex: /^(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/,
    error:
      "Password must be at least 8 characters, include a number and a special character.",
  },
  text: {
    regex: /^.*$/,
    error: "Only letters, numbers, spaces, and colons are allowed.",
  },
  numberOnly: {
    regex: /^[0-9]+(\.[0-9]+)?$/,
    error: "Only numbers are allowed.",
  },
};

// <-- helper function added for editor validation
const isEditorEmpty = (html: string | null | undefined) => {
  if (!html) return true;

  if (typeof window === "undefined") {
    // SSR fallback
    return html === "" || html === "<br>" || html === "<p><br></p>";
  }

  const div = document.createElement("div");
  div.innerHTML = html;

  const text = div.textContent?.replace(/\u200B/g, "").trim(); // remove zero-width spaces
  return text === "";
};

function validator(fields: ValidationField[]): ValidationResult {
  const errors: Record<string, string> = {};

  fields.forEach(({ key }) => {
    errors[key] = "";
  });

  const fieldMap: Record<string, string | File | null | any[] | number> = {};

  fields.forEach(({ key, value }) => {
    fieldMap[key] = value;
  });

  fields.forEach(
    ({
      key,
      value,
      type,
      customPattern,
      customError,
      minLength,
      maxLength,
      fileType,
      range,
    }) => {
      const trimmed = typeof value === "string" ? value.trim() : value;

      if (type === "array") {
        if (!Array.isArray(value) || value.length === 0) {
          errors[key] = "Field must not be empty.";
        }
        return;
      }

      if (type === "text") {
        if (!trimmed || trimmed === "") {
          errors[key] = "Field is required.";
          return;
        }
        if (typeof trimmed === "string") {
          if (minLength && trimmed.length < minLength) {
            errors[key] = `Minimum length is ${minLength} characters.`;
            return;
          }
          if (maxLength && trimmed.length > maxLength) {
            errors[key] = `Maximum length is ${maxLength} characters.`;
            return;
          }
        }
        return;
      }

      if (type === "date" && typeof trimmed === "string") {
        if (!trimmed || trimmed === "") {
          errors[key] = "Field is required.";
          return;
        }
        const date = new Date(trimmed);
        if (isNaN(date.getTime()) || !/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
          errors[key] = "Invalid date format.";
        }
        return;
      }

      if (type === "password") {
        if (!trimmed || trimmed === "") {
          errors[key] = "Field is required.";
          return;
        }
        const { regex, error } = patterns.password;
        if (typeof trimmed === "string" && !regex.test(trimmed)) {
          errors[key] = error;
        }
        return;
      }

      if (type === "confirmPassword") {
        if (!trimmed || trimmed === "") {
          errors[key] = "Field is required.";
          return;
        }
        if (trimmed !== fieldMap["password"]) {
          errors[key] = "Passwords do not match.";
        }
        return;
      }

      if (type === "file") {
        const file = trimmed as File | null;

        if (!file) {
          errors[key] = "Field is required.";
          return;
        }

        const mime = file.type;

        const typeMap: Record<string, string[]> = {
          image: ["image/jpeg", "image/png", "image/gif", "image/webp"],
          pdf: ["application/pdf"],
          doc: [
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          ],
          video: ["video/mp4", "video/webm", "video/quicktime"],
        };

        if (fileType && typeMap[fileType]) {
          if (!typeMap[fileType].includes(mime)) {
            errors[key] = `Unsupported ${fileType} file type.`;
            return;
          }
        }
        return;
      }

      // <-- added editor validation block
      if (type === "editor") {
        if (typeof trimmed !== "string" || isEditorEmpty(trimmed)) {
          errors[key] = "Field is required.";
        } else if (minLength && trimmed.length < minLength) {
          errors[key] = `Minimum length is ${minLength} characters.`;
        }
        return;
      }

      // Generic pattern validation for text/numberOnly/customPattern
      let pattern: RegExp;
      let errorMessage: string;

      if (customPattern) {
        pattern = new RegExp(customPattern);
        errorMessage = customError || "Invalid input.";
      } else if (patterns[type as keyof typeof patterns]) {
        pattern = patterns[type as keyof typeof patterns].regex;
        errorMessage = patterns[type as keyof typeof patterns].error;
      } else {
        errors[key] = "Unknown validation type.";
        return;
      }

      if (typeof trimmed === "string") {
        if (minLength && trimmed.length < minLength) {
          errors[key] = `Minimum length is ${minLength} characters.`;
        } else if (!pattern.test(trimmed)) {
          errors[key] = errorMessage;
        }
      }

      if (type === "numberOnly" && typeof trimmed === "string") {
        const numberValue = parseInt(trimmed, 10);

        if (isNaN(numberValue)) {
          errors[key] = "Input must be a valid number.";
          return;
        }

        if (range) {
          const [min, max] = range.split("-").map(Number);
          if (isNaN(min) || isNaN(max)) {
            errors[key] = "Invalid range format.";
            return;
          }

          if (numberValue < min || numberValue > max) {
            errors[key] = `Value must be between ${min} and ${max}.`;
          }
        }
      }
    },
  );

  const isValid = Object.values(errors).every((error) => error === "");

  return { errors, isValid };
}

export default validator;
